import { Injectable } from '@angular/core';
import { reduce } from 'lodash-es';
import numeral from 'numeral';

// todo: this utility library doesn't handle the optional digit before the decimal point correctly
// (it always has a digit before the decimal point, even if it's a leading 0)
// for example:
// format(0, '[0][.]0') => '0', i would expect ''
// format(0.56, '[0][.]0') => '0.6', i would expect '.6'

// you can get the result without the leading 0, by doing this:
// format(0, '[.]0') => ''
// format(0.56, '[.]0') => '.6'
// these will work as expected,
// but then problem is this:
// format(6.56, '[.]0') => '.6', i would expect '6.6'
// it just never shows anything to the left of the decimal point\

// and it's not trivial to just remove the 0 out of there in this function, since the leading zero could be anywhere in the
// result string, since it's based on what format was passed in
// (the format could have a currency symbol or '-' sign at the start of the string, among other things)
// '0.60' or '-0.60' or '$ 0.60' or '$ -0.60'
// these 4 examples all have leading zeroes in a different index in the result string

// Other Info:
// ===========
// c# formats i've seen in our backend codebase:
// 1. ####.##
// 2. #.##
// 3. .##
// these 3 examples are identical in c#
// they all would convert to [0].[00] for this numeraljs formatter, which translates to
// optional digit followed by decimal, and it has a max of 2 optional digits after the decimal point

// this regex will handle most or all cases where where it could return '0', when it really should return ''
// because all digits in the format are optional
// this is important only because there are certain cases where if a number value is 0, we could check the result
// to see if it's just an empty string, or if it actually has a value
// [0] at start, followed by ( ([.] and anything after it) OR .([0*])? )?
const zeroToEmptyRegex = /^\[0]((\[\.].*)|(\.(\[0*])?))?$/;

// Keep previous functionality to avoid unknown side effects.
function numeraljsFilter(input: any, format: string) {
  if (input == null) {
    return input;
  }

  return numeral(input).format(format);
}

// This service is an abstraction around the third party numeraljsFilter,
// It is useful in case we want to swap it out with another implementation
@Injectable({
  providedIn: 'root',
})
export class NumUtil {
  static readonly ajsFactoryName = 'NumUtil';

  format(input: any, format: string) {
    let result = numeraljsFilter(input, format);

    // if the regex matches, return empty string ('') instead of '0'
    if (result && result === '0' && zeroToEmptyRegex.test(format)) {
      result = '';
    }
    return result;
  }

  // precision multiplication, different from just doing num1 * num2
  // check for example 18.7 * 1.5 === 28.049999999999997 vs multiply(18.7, 1.5) === 28.05
  // can pass more than 2 arguments, and they will all be multiplied, ex: multiply(2, 3, 8) === 48
  multiply(...args: any[]) {
    const result = reduce(args, (cumulative, num) => {
      return cumulative.multiply(num);
    }, numeral(1)).value();

    return result ?? 0;
  }

  // same idea as multiply function => more precise than num1 / num2
  // 6.9 / 3 === 2.3000000000000003 vs divide(6.9, 3) === 2.3
  divide(num1: any, num2: any) {
    const result = numeral(num1).divide(num2).value();
    return result ?? 0;
  }

  // same idea as multiply function => more precise than num1 + num2
  // 0.1 + 0.2 === 0.30000000000000004 vs add(0.1, 0.2) === 0.3
  add(...args: any[]) {
    const result = reduce(args, (cumulative, num) => {
      return cumulative.add(num);
    }, numeral(0)).value();

    return result ?? 0;
  }

  // same idea as multiply function => more precise than num1 - num2
  // 0.1 - 0.3 === -0.19999999999999998 vs subtract(0.1, 0.3) === -0.2
  subtract(num1: any, num2: any) {
    const result = numeral(num1).subtract(num2).value();
    return result ?? 0;
  }
}
